import { Component, Inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FeedStockLoadAdditionalRevenue } from '../../../interfaces/pricematrixmodel';
import { LookUpService } from '../../../services/lookupservice';
import { ConfirmationDialogData, ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { numericInputLimit } from '../../shared/commonutils/helper';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatButtonModule } from '@angular/material/button';
import { RPCInput, RPCTypesTableType } from '../../../interfaces/lookup';
import { MatInputModule } from '@angular/material/input';
import { FeedstockService } from '../../../services/feedstockservice';

@Component({
  selector: 'app-add-edit-rpc',
  imports: [
    MatDialogModule, 
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    CommonModule,
    FormsModule,
    MatTableModule,
    ReactiveFormsModule,
    DragDropModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule
  ],
  templateUrl: './add-edit-rpc.component.html',
  styleUrl: './add-edit-rpc.component.scss'
})
export class AddEditRpcComponent implements OnInit {
  editForm!: FormGroup;
  workOrderNumber: string = ""
  loadSchedId: number = 0;
  rowId: number = 0;
  rpcTypes: any[] = [];
  displayValue=signal<string>('');
  editRow: RPCTypesTableType | null = null;
  dataSource = new MatTableDataSource<RPCTypesTableType>([]);
  displayedColumns: string[] = ['actions', 'rpcTypeId', 'rpCcount'];
  submitted: boolean = false;
  _dirty = false;
  btnName: string = 'Add';
  tempRowId: number = -1;
  constructor(
    public dialogRef: MatDialogRef<AddEditRpcComponent>,
    private lookUpService: LookUpService, private feedstockService: FeedstockService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public dialogData: RPCInput,
    private fb: FormBuilder
  ){
    console.log("input dialog data", dialogData);
    this.workOrderNumber = dialogData.name;
    this.loadSchedId = dialogData.loadScheduleId;
    this.editForm = this.fb.group({
      id: [this.rowId],
      rpcType: [null, [Validators.required]],
      rpCcount: [null, [Validators.required]],
      fsLoadScheduleId: [this.loadSchedId]
    });

    this.editForm.valueChanges.subscribe(values => {
      if (!this.editRow) return;  // Add mode → dirty is set by onSubmitRow()
      const changed =
        values.rpcType !== this.editRow.rpcTypeId ||
        values.rpCcount   !== this.editRow.rpCcount;
      if (changed) {
        this._dirty = true;
      }
    });
    
    const seed = Array.isArray(dialogData?.gridData) ? dialogData.gridData : [];
    this.dataSource.data = seed.map(r => ({ ...r }));
  }

  ngOnInit(): void {
    this.getRPCTypes();
  }

  getRPCTypes(): void {
    this.lookUpService.getRPCTypes().subscribe({
      next: (response) => {
        this.rpcTypes = response;
      }
    })
  }
  
  onSubmitRow(): void {
    this.submitted = true;
    this.editForm.markAllAsTouched();

    if (this.editForm.invalid) {
      return;
    }

    const { rpcType, rpCcount } = this.editForm.value;
    const currentData = [...this.dataSource.data];

    if (this.editRow) {
      const index = currentData.indexOf(this.editRow);
      if (index > -1) {
        currentData[index] = {
          ...this.editRow,
          rpcTypeId: rpcType,
          rpCcount
        };
        this.dataSource.data = currentData;
      }
    } else {
      const newRow: RPCTypesTableType = {
        id: this.tempRowId--,
        rpcTypeId: rpcType,
        rpCcount,
        fsLoadScheduleId: this.loadSchedId
      } as RPCTypesTableType;
      this.dataSource.data = [...currentData, newRow];
    }
    this._dirty = false;
    this.resetForm();
  }
  
  onEditRow(row: RPCTypesTableType): void {
    this.btnName = 'Update';
    if (!row || row.id === 0) return;   // hard guard in addition to [disabled]
    this.editRow = row;
    this.submitted = false;
    this.editForm.patchValue({
      id: row.id,
      rpcType: row.rpcTypeId,
      rpCcount: row.rpCcount,
      fsLoadScheduleId: this.loadSchedId
    });
    this.editForm.markAsPristine();
    this.editForm.markAsUntouched();
  }

  private resetForm(): void {
    this.editRow = null;
    this.submitted = false;
    this.editForm.reset({
      id: 0,
      rpcType: null,
      count: null,
      fsLoadScheduleId: this.loadSchedId
    });
    this.displayValue.set('');
    this.btnName = 'Add';
  }

  onSave(){
    this.dialogRef.close(this.dataSource.data.map(r => ({ ...r })));
  } 

  onDeleteRow(id: number, name: string | null, row: RPCTypesTableType): void {
    const dialogData: ConfirmationDialogData = {
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete "${name || 'this item'}"?`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: 'warn'
    };

    this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      panelClass: 'delete-confirmation-dialog',
      data: dialogData,
      disableClose: true
    }).afterClosed().subscribe(result => {
      if (result) {
        this._dirty = true;
        if (this.editRow === row) this.resetForm();
        this.feedstockService.deleteRPCById(id).subscribe({
          next: () => {
            this.dataSource.data = this.dataSource.data.filter(r => r !== row);            
          }
        });
      }
    });
  }

  onClose(): void {
    this.dialogRef.close(false);
  }

  roundOff(event: Event, maxValue: number, maxDecimals: number, controlName: string): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;
    
    val = numericInputLimit(event, maxValue, maxDecimals);
    
    this.displayValue.set(val);
    input.value = val;
    
    const isValueEmpty = val === '' || val === '.';
    if (isValueEmpty) {
      this.editForm.get(controlName)?.setValue(null, { emitEvent: false });
    } else if (val.endsWith('.')) {
      this.editForm.get(controlName)?.setErrors({ 'incompleteDecimal': true });
    } else {
      const parsedValue = parseFloat(val);
      this.editForm.get(controlName)?.setValue(parsedValue, { emitEvent: false });
    }
    this._dirty = true;
    requestAnimationFrame(() => {
      if (document.activeElement === input) {
        input.value = val;
      }
    });
  }

  getRPCTypeName(id: number | null): string {
    if (id === null || id === undefined) {
      return '';
    }
    const rpcType = this.rpcTypes.find(et => et.id === id);
    return rpcType ? rpcType.name : '';
  }
  
  trackByRpcId(index: number, item: RPCTypesTableType): number {
    return item.id;
  }

  get canSave(): boolean {
    return this._dirty;
  }
}


